import { GridColDef } from '@mui/x-data-grid-pro'
import { Avatar } from '@mui/material'
import { TFunction } from 'i18next'
import IconButton from '@/shared/components/IconButton/IconButton'
import Chip from '@/shared/components/Chip/Chip'
import Rating from '@/shared/components/Rating/Rating'
import { initialsOf } from '@/shared/utils/initials'
import { EmployeeRole, EmployeeStatus } from './hooks/useEmployeeMetrics'
import styles from './EmployeeColumns.module.scss'

/**
 * The role a row shows is the employment profile's position, so it is written
 * with the same copy the employment form offers rather than a second wording.
 */
const ROLE_KEYS: Record<EmployeeRole, string> = {
  Waiter: 'TABLE.EMPLOYEES.POSITIONS.WAITER',
  Chef: 'TABLE.EMPLOYEES.POSITIONS.CHEF',
  Bartender: 'TABLE.EMPLOYEES.POSITIONS.BARTENDER',
  Host: 'TABLE.EMPLOYEES.POSITIONS.HOST',
  Manager: 'TABLE.EMPLOYEES.POSITIONS.MANAGER'
}

const STATUS_KEYS: Record<EmployeeStatus, string> = {
  Active: 'EMPLOYEES.STATUS.ACTIVE',
  'On leave': 'EMPLOYEES.STATUS.ON_LEAVE',
  Off: 'EMPLOYEES.STATUS.OFF'
}

export const getColumnsConfig = (
  translations: TFunction<'translation', undefined>,
  handleOnEdit?: (id: string) => void,
  handleOnDelete?: (id: string) => void
) => {
  const t = (key: string) => translations(key)

  const renderHeader = (label?: string) => (
    <p className={`${styles.tableHeaderText} body-bold-medium`}>{label}</p>
  )

  const columns: GridColDef[] = [
    {
      field: 'picture',
      headerName: t('TABLE.EMPLOYEES.HEADERS.PICTURE'),
      resizable: false,
      hideable: false,
      editable: false,
      headerClassName: styles.tableHeader,
      cellClassName: styles.tableCell,
      width: 80,
      disableColumnMenu: true,
      sortable: false,
      renderHeader: params => renderHeader(params.colDef.headerName),
      renderCell: params => {
        const initials = initialsOf(params.row.name)
        return (
          <Avatar
            src={params.value}
            alt="employee-image"
            sx={{ width: '2.5rem', height: '2.5rem' }}
          >
            {initials ? (
              <p className="body-bold-medium">{initials}</p>
            ) : (
              <i className="las la-user icon-big" />
            )}
          </Avatar>
        )
      }
    },
    {
      field: 'name',
      headerName: t('TABLE.EMPLOYEES.HEADERS.NAME'),
      flex: 1.4,
      hideable: false,
      editable: false,
      headerClassName: styles.tableHeader,
      cellClassName: styles.tableCell,
      disableColumnMenu: true,
      minWidth: 120,
      renderHeader: params => renderHeader(params.colDef.headerName)
    },
    {
      field: 'role',
      headerName: t('TABLE.EMPLOYEES.HEADERS.ROLE'),
      flex: 1,
      editable: false,
      headerClassName: styles.tableHeader,
      cellClassName: styles.tableCell,
      disableColumnMenu: true,
      minWidth: 110,
      renderHeader: params => renderHeader(params.colDef.headerName),
      renderCell: params => {
        const role = params.value as EmployeeRole
        return (
          <Chip label={t(ROLE_KEYS[role] ?? ROLE_KEYS.Waiter)} color="primary" />
        )
      }
    },
    {
      field: 'email',
      headerName: t('TABLE.EMPLOYEES.HEADERS.EMAIL'),
      flex: 1.4,
      editable: false,
      headerClassName: styles.tableHeader,
      cellClassName: styles.tableCell,
      disableColumnMenu: true,
      minWidth: 140,
      renderHeader: params => renderHeader(params.colDef.headerName)
    },
    {
      field: 'serviceRating',
      headerName: t('TABLE.EMPLOYEES.HEADERS.RATING'),
      flex: 1,
      editable: false,
      headerClassName: styles.tableHeader,
      cellClassName: styles.tableCell,
      disableColumnMenu: true,
      minWidth: 130,
      align: 'left',
      headerAlign: 'left',
      renderHeader: params => renderHeader(params.colDef.headerName),
      renderCell: params => (
        <div className={styles.ratingCell}>
          <Rating
            key={`rating-${params.id}`}
            defaultValue={params.value as number}
            size="0.9rem"
            precision={0.1}
            isReadOnly
          />
          <span className="body-small">
            {(params.value as number)?.toFixed(1)}
          </span>
        </div>
      )
    },
    {
      field: 'ordersServed',
      headerName: t('TABLE.EMPLOYEES.HEADERS.ORDERS'),
      flex: 1,
      editable: false,
      headerClassName: styles.tableHeader,
      cellClassName: styles.tableCell,
      disableColumnMenu: true,
      minWidth: 140,
      align: 'left',
      headerAlign: 'left',
      renderHeader: params => renderHeader(params.colDef.headerName)
    },
    {
      field: 'hoursThisWeek',
      headerName: t('TABLE.EMPLOYEES.HEADERS.HOURS'),
      flex: 1,
      editable: false,
      headerClassName: styles.tableHeader,
      cellClassName: styles.tableCell,
      disableColumnMenu: true,
      minWidth: 130,
      align: 'left',
      headerAlign: 'left',
      valueFormatter: (value: number) =>
        typeof value === 'number' ? `${value}h` : value,
      renderHeader: params => renderHeader(params.colDef.headerName)
    },
    {
      field: 'status',
      headerName: t('TABLE.EMPLOYEES.HEADERS.STATUS'),
      flex: 0.8,
      editable: false,
      headerClassName: styles.tableHeader,
      cellClassName: styles.tableCell,
      disableColumnMenu: true,
      minWidth: 110,
      renderHeader: params => renderHeader(params.colDef.headerName),
      renderCell: params => {
        const status = params.value as EmployeeStatus
        return (
          // White for every status, including Active. Orange is this app's
          // attention colour, it marks a missing translation, a falling
          // trend, and somebody simply being at work is the ordinary case,
          // with nothing to draw the eye to. It also matches the role chip
          // sitting two columns to the left, which the orange one clashed
          // with. The statuses are told apart by their label.
          <Chip
            label={t(STATUS_KEYS[status] ?? STATUS_KEYS.Active)}
            color="primary"
          />
        )
      }
    },
    {
      field: 'action',
      headerName: t('TABLE.EMPLOYEES.HEADERS.ACTIONS'),
      resizable: false,
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      editable: false,
      headerClassName: styles.tableHeader,
      cellClassName: styles.tableCell,
      disableColumnMenu: true,
      disableReorder: true,
      sortable: false,
      pinnable: true,
      hideable: false,
      renderHeader: params => (
        <p
          className={`${styles.tableHeaderText} body-bold-medium hideRightSeparator`}
        >
          {params.colDef.headerName}
        </p>
      ),
      renderCell: params => (
        <div className={styles.tableActionContainer}>
          <IconButton
            icon="las la-edit icon-medium"
            onClick={() => handleOnEdit?.(params.row.id)}
            color="primary"
          />
          <IconButton
            icon="las la-trash icon-medium"
            onClick={() => handleOnDelete?.(params.row.id)}
            color="primary"
          />
        </div>
      )
    }
  ]
  return columns
}
